import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, standalone, embeddable lead-capture form (no auth, no app chrome).
 * Rendered at /f/:formId and inside the iframe embed snippet.
 */
const PublicForm = () => {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues] = useState({});
  const [hp, setHp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const fetchForm = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/forms/${formId}`);
      setForm(res.data);
    } catch {
      setNotFound(true);
    } finally { setLoading(false); }
  }, [formId]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  const setVal = (key, v) => setValues(prev => ({ ...prev, [key]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/forms/${formId}/submit`, { data: values, hp });
      if (res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
        return;
      }
      setDone(res.data.message || 'Thank you!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-7 h-7 animate-spin text-violet-600" /></div>;
  }
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="public-form-notfound">
        <p className="text-slate-500">This form is no longer available.</p>
      </div>
    );
  }

  const renderField = (f) => {
    const v = values[f.key] ?? '';
    const base = 'w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500';
    if (f.type === 'consent') {
      return (
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" checked={!!v} onChange={e => setVal(f.key, e.target.checked)} required={f.required} className="mt-0.5 w-4 h-4 accent-violet-600" data-testid={`field-${f.key}`} />
          <span className="text-xs text-slate-600 leading-relaxed">{form.consent_text || f.label}</span>
        </label>
      );
    }
    if (f.type === 'textarea') {
      return <textarea value={v} onChange={e => setVal(f.key, e.target.value)} required={f.required} rows={4} placeholder={f.placeholder || ''} className={base} data-testid={`field-${f.key}`} />;
    }
    if (f.type === 'select') {
      return (
        <select value={v} onChange={e => setVal(f.key, e.target.value)} required={f.required} className={base} data-testid={`field-${f.key}`}>
          <option value="">Select…</option>
          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === 'checkbox') {
      return (
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" checked={!!v} onChange={e => setVal(f.key, e.target.checked)} required={f.required} className="w-4 h-4 accent-violet-600" data-testid={`field-${f.key}`} />
          <span className="text-sm text-slate-700">{f.label}</span>
        </label>
      );
    }
    const inputType = f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : 'text';
    return <input type={inputType} value={v} onChange={e => setVal(f.key, e.target.value)} required={f.required} placeholder={f.placeholder || ''} className={base} data-testid={`field-${f.key}`} />;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-start sm:items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-6 sm:p-8" data-testid="public-form">
        {done ? (
          <div className="text-center py-8" data-testid="form-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Thank you</h2>
            <p className="text-sm text-slate-500">{done}</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-slate-900 mb-1">{form.name}</h1>
            <div className="h-1 w-12 bg-violet-600 rounded-full mb-6" />
            <form onSubmit={submit} className="space-y-4">
              {/* Honeypot - visually hidden */}
              <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
              {(form.fields || []).map(f => (
                <div key={f.key}>
                  {!['consent', 'checkbox'].includes(f.type) && (
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {f.label}{f.required && <span className="text-rose-500"> *</span>}
                    </label>
                  )}
                  {renderField(f)}
                </div>
              ))}
              {error && <p className="text-sm text-rose-600" data-testid="form-error">{error}</p>}
              <button type="submit" disabled={submitting} className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-lg py-2.5 text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="form-submit-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : (form.submit_label || 'Submit')}
              </button>
            </form>
            <p className="text-[11px] text-slate-400 text-center mt-5">Powered by Care Tech Compare</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicForm;
